import * as ethers from 'ethers';
import React, { Dispatch } from 'react';
import debug from 'debug';
import { ConditionWaiter } from '@common/ConditionWaiter';
import { UserAccount } from '@common/UserAccount';
import { Action } from '@context/GenerateContext';
import { UserService, WithdrawStatus } from './UserService';
import { UserActionType } from './UserActionType';
import { UserPermission } from '@common/UserPermission';
import { LoginState, loginStateFinished, loginStateInProgress } from '@common/LoginState';
import { AnalyticsEventName } from '@services/analytics/AnalyticsEventName';
import { mainSuite } from '@services/ServiceFactory';
import { ChainalysisService } from '../chainalysis/ChainalysisService';
import { UserActionProps } from './UserContext';
import { environment } from '@environment';
import { SvsNavbarEvent, Wallet } from '@play-co/svs-navbar';

const log = debug('app:service:MetaMaskUserService');

export class MetaMaskUserService implements UserService {
  readonly logInPopUp = true;

  private initWaiter = new ConditionWaiter(false);
  private loginStateWaiter = new ConditionWaiter(LoginState.Idle, loginStateFinished);
  private userDispatchWaiter = new ConditionWaiter<React.Dispatch<Action<UserActionType, UserActionProps>>>(null);
  private userStateWaiter = new ConditionWaiter<UserAccount>(null);

  constructor(
    private chainalysisService: ChainalysisService
  ) {
    this.initLoggedInUser();
  }

  hasInitialized(): boolean {
    return this.initialized;
  }
  waitForInit(): Promise<void> {
    return this.initWaiter.wait() as unknown as Promise<void>;
  }

  setDispatch(dispatch: Dispatch<Action<UserActionType, UserActionProps>>): void {
    this.userDispatchWaiter.set(dispatch);
    this.checkForInit();
  }
  setUserState(state: UserAccount): void {
    this.userStateWaiter.set(state);
    this.checkForInit();
  }
  signUp(): void {
    this.startLogin();
  }
  logIn(): void {
    this.startLogin();
  }
  logOut(): void {
    this.startLogout(false);
  }

  private get initialized(): boolean {
    return this.initWaiter.get();
  }

  private get userDispatch(): React.Dispatch<Action<UserActionType, UserActionProps>> {
    return this.userDispatchWaiter.get();
  }

  private get userState(): UserAccount {
    return this.userStateWaiter.get();
  }

  private get loginState(): LoginState {
    return this.loginStateWaiter.get();
  }

  private async waitForStateAndDispatch(): Promise<void> {
    await Promise.all([
      this.userDispatchWaiter.wait(),
      this.userStateWaiter.wait(),
    ]);
  }

  private checkForInit(): void {
    if (this.initialized) { return; }
    if (!this.userState) { return; }
    if (!this.userDispatch) { return; }
    if (!loginStateFinished(this.loginState)) { return; }

    this.initWaiter.set(true);
  }

  private setLoginState(loginState: LoginState) {
    this.loginStateWaiter.set(loginState);
    this.userDispatch({ type: UserActionType.UpdateLoginState, loginState });
  }

  private async populateUser(wallet?: Wallet): Promise<void> {
    if (!wallet) {
      wallet = await mainSuite.navbarService.api.getWallet();
    }
    if (!wallet?.flags.loggedIn) {
      // not sure what happened here, but we don't have wallet info
      log('Missing wallet info');
      await this.startLogout(false);
      return;
    }
    const caSafe = await this.chainalysisService.isWalletAddressSafe(wallet.address);
    const permissions = [UserPermission.User];
    // mostly for mocking
    if (wallet.address === environment.serviceAddress) {
      permissions.push(UserPermission.Admin);
      permissions.push(UserPermission.Service);
    }
    this.userDispatch({
      type: UserActionType.LogIn,
      caSafe,
      address: wallet.address,
      name: wallet.address,
      wallet: wallet,
      permissions,
    });
  }

  private async initLoggedInUser(): Promise<void> {
    await mainSuite.navbarService.api.init();
    const loggedIn = await mainSuite.navbarService.api.isLoggedIn();
    if (loggedIn) {
      this.setLoginState(LoginState.LoggingIn);
      await this.populateUser();
    } else {
      await this.startLogout(true);
    }
    mainSuite.navbarService.on(SvsNavbarEvent.LoginStateChanged, (loginState) => {
      log('Login state changed:', loginState);
    });
    mainSuite.navbarService.on(SvsNavbarEvent.WalletChanged, (wallet) => {
      if (wallet.flags.loggedIn) {
        log('logging in with', wallet.address);
        this.setLoginState(LoginState.LoggingIn);
        this.populateUser(wallet);
      } else {
        log('logging out');
        this.startLogout(false);
      }
    });
  }
  
  private async startLogin(): Promise<void> {
    mainSuite.analyticsService.track(AnalyticsEventName.WalletConnectStart);
    this.setLoginState(LoginState.LoggingIn);
    await this.waitForInit();
    try {
      const loggedIn = await mainSuite.navbarService.api.logIn();
      if (!loggedIn) {
        // most likely cancelled log in
        await this.startLogout(false);
        return;
      }
      await this.populateUser();
    } catch (e) {
      log('Error occurred while logging in:', e);
      mainSuite.analyticsService.track(AnalyticsEventName.WalletConnectError, {
        errorCode: e.code,
        errorMessage: e.message,
        errorStack: e.stack
      });
      this.setLoginState(LoginState.LoggedOut);
    }
  }

  private async startLogout(skipWait: boolean): Promise<void> {
    await this.waitForStateAndDispatch();
    this.setLoginState(LoginState.LoggingOut);
    this.userDispatch({ type: UserActionType.LogOut });
  }
}
